import React, { useState } from 'react';
import {
  Key,
  Eye,
  EyeOff,
  Save,
  Trash2,
  TestTube,
  Check,
  X,
  AlertCircle,
  Settings,
  Shield,
  ExternalLink,
  Loader
} from 'lucide-react';
import { useApiKeys, ApiKeys } from '../contexts/ApiKeyContext';

interface ApiSettingsModalProps {
  onClose: () => void;
}

interface ApiProvider {
  id: keyof ApiKeys;
  name: string;
  icon: string;
  models: string[];
  placeholder: string;
  helpUrl: string;
  description: string;
  color: string;
}

const ApiSettingsModal: React.FC<ApiSettingsModalProps> = ({ onClose }) => {
  const {
    apiKeys,
    setApiKey,
    clearApiKey,
    clearAllApiKeys,
    testConnection,
    validateApiKey
  } = useApiKeys();

  const [showKeys, setShowKeys] = useState<Record<string, boolean>>({});
  const [tempKeys, setTempKeys] = useState<ApiKeys>({ ...apiKeys });
  const [testing, setTesting] = useState<Record<string, boolean>>({});
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; message: string }>>({});

  const providers: ApiProvider[] = [
    {
      id: 'openai',
      name: 'OpenAI',
      icon: '🤖',
      models: ['GPT-4', 'GPT-4 Vision', 'DALL-E 3'],
      placeholder: 'sk-...',
      helpUrl: 'https://platform.openai.com/api-keys',
      description: 'Powers GPT-4, DALL-E 3, and advanced AI features',
      color: 'green'
    },
    {
      id: 'anthropic',
      name: 'Anthropic',
      icon: '🧠',
      models: ['Claude 3 Opus', 'Claude 3 Sonnet'],
      placeholder: 'sk-ant-...',
      helpUrl: 'https://console.anthropic.com/settings/keys',
      description: 'Powers Claude models for deep analysis and creative writing',
      color: 'purple'
    },
    {
      id: 'google',
      name: 'Google AI',
      icon: '✨',
      models: ['Gemini Pro', 'ImageFX', 'Veo'],
      placeholder: 'AIza...',
      helpUrl: 'https://makersuite.google.com/app/apikey',
      description: 'Powers Gemini, ImageFX, and Google AI services',
      color: 'blue'
    },
    {
      id: 'stability',
      name: 'Stability AI',
      icon: '🎨',
      models: ['Stable Diffusion XL', 'SDXL Turbo'],
      placeholder: 'sk-...',
      helpUrl: 'https://platform.stability.ai/account/keys',
      description: 'Powers Stable Diffusion image generation',
      color: 'orange'
    },
    {
      id: 'replicate',
      name: 'Replicate',
      icon: '🔄',
      models: ['Midjourney', 'Various Models'],
      placeholder: 'r8_...',
      helpUrl: 'https://replicate.com/account/api-tokens',
      description: 'Access to Midjourney and community models',
      color: 'indigo'
    },
    {
      id: 'mistral',
      name: 'Mistral AI',
      icon: '🌊',
      models: ['Mistral Medium', 'Mixtral'],
      placeholder: 'API key',
      helpUrl: 'https://console.mistral.ai/api-keys',
      description: 'Fast and efficient language models',
      color: 'cyan'
    },
    {
      id: 'meta',
      name: 'Meta (LLaMA)',
      icon: '🦙',
      models: ['LLaMA 2', 'CodeLlama'],
      placeholder: 'API key',
      helpUrl: 'https://ai.meta.com/',
      description: 'Open-source LLaMA models',
      color: 'pink'
    }
  ];

  const handleKeyChange = (provider: keyof ApiKeys, value: string) => {
    setTempKeys(prev => ({
      ...prev,
      [provider]: value
    }));
  };

  const handleSaveKey = async (provider: keyof ApiKeys) => {
    const key = tempKeys[provider];
    if (!key) return;

    const isValid = await validateApiKey(provider, key);
    if (isValid) {
      setApiKey(provider, key);
      setTestResults(prev => ({
        ...prev,
        [provider]: { success: true, message: 'API key saved successfully!' }
      }));
    } else {
      setTestResults(prev => ({
        ...prev,
        [provider]: { success: false, message: 'Invalid API key format' }
      }));
    }
  };

  const handleTestConnection = async (provider: keyof ApiKeys) => {
    setTesting(prev => ({ ...prev, [provider]: true }));
    setTestResults(prev => ({ ...prev, [provider]: { success: false, message: 'Testing...' } }));

    const result = await testConnection(provider);
    
    setTestResults(prev => ({
      ...prev,
      [provider]: result
    }));
    setTesting(prev => ({ ...prev, [provider]: false }));

    setTimeout(() => {
      setTestResults(prev => {
        const newResults = { ...prev };
        delete newResults[provider];
        return newResults;
      });
    }, 5000);
  };

  const handleClearKey = (provider: keyof ApiKeys) => {
    clearApiKey(provider);
    setTempKeys(prev => {
      const newKeys = { ...prev };
      delete newKeys[provider];
      return newKeys;
    });
    setTestResults(prev => {
      const newResults = { ...prev };
      delete newResults[provider];
      return newResults;
    });
  };

  const toggleShowKey = (provider: string) => {
    setShowKeys(prev => ({
      ...prev,
      [provider]: !prev[provider]
    }));
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string; button: string }> = {
      green: {
        bg: 'bg-green-50',
        border: 'border-green-200',
        text: 'text-green-700',
        button: 'bg-green-600 hover:bg-green-700'
      },
      purple: {
        bg: 'bg-purple-50',
        border: 'border-purple-200',
        text: 'text-purple-700',
        button: 'bg-purple-600 hover:bg-purple-700'
      },
      blue: {
        bg: 'bg-blue-50',
        border: 'border-blue-200',
        text: 'text-blue-700',
        button: 'bg-blue-600 hover:bg-blue-700'
      },
      orange: {
        bg: 'bg-orange-50',
        border: 'border-orange-200',
        text: 'text-orange-700',
        button: 'bg-orange-600 hover:bg-orange-700'
      },
      indigo: {
        bg: 'bg-indigo-50',
        border: 'border-indigo-200',
        text: 'text-indigo-700',
        button: 'bg-indigo-600 hover:bg-indigo-700'
      },
      cyan: {
        bg: 'bg-cyan-50',
        border: 'border-cyan-200',
        text: 'text-cyan-700',
        button: 'bg-cyan-600 hover:bg-cyan-700'
      },
      pink: {
        bg: 'bg-pink-50',
        border: 'border-pink-200',
        text: 'text-pink-700',
        button: 'bg-pink-600 hover:bg-pink-700'
      }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Settings className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">API Key Management</h2>
                <p className="text-sm opacity-90 mt-1">Configure your AI model API keys</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Security Notice */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <Shield className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-blue-900 font-medium">Your API keys are stored locally</p>
              <p className="text-xs text-blue-700 mt-1">
                Keys are encrypted and stored in your browser's local storage. They never leave your device.
              </p>
            </div>
          </div>

          {/* Provider Cards */}
          <div className="space-y-4">
            {providers.map(provider => {
              const colors = getColorClasses(provider.color);
              const hasKey = !!apiKeys[provider.id];
              const testResult = testResults[provider.id];

              return (
                <div
                  key={provider.id}
                  className={`border rounded-xl p-4 transition-all ${
                    hasKey ? colors.bg + ' ' + colors.border : 'border-gray-200'
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{provider.icon}</span>
                      <div>
                        <h3 className="font-semibold text-gray-900">{provider.name}</h3>
                        <p className="text-xs text-gray-600">{provider.description}</p>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {provider.models.map(model => (
                            <span
                              key={model}
                              className={`text-xs px-2 py-0.5 rounded-full ${
                                hasKey
                                  ? colors.bg + ' ' + colors.text
                                  : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {model}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <a
                      href={provider.helpUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Get API Key"
                    >
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </div>

                  <div className="space-y-3">
                    <div className="flex gap-2">
                      <div className="flex-1 relative">
                        <input
                          type={showKeys[provider.id] ? 'text' : 'password'}
                          value={tempKeys[provider.id] || ''}
                          onChange={(e) => handleKeyChange(provider.id, e.target.value)}
                          placeholder={provider.placeholder}
                          className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <button
                          onClick={() => toggleShowKey(provider.id)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        >
                          {showKeys[provider.id] ? (
                            <EyeOff className="w-4 h-4" />
                          ) : (
                            <Eye className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                      
                      <button
                        onClick={() => handleSaveKey(provider.id)}
                        disabled={!tempKeys[provider.id]}
                        className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${colors.button}`}
                      >
                        <Save className="w-4 h-4" />
                      </button>
                      
                      {hasKey && (
                        <>
                          <button
                            onClick={() => handleTestConnection(provider.id)}
                            disabled={testing[provider.id]}
                            className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50"
                          >
                            {testing[provider.id] ? (
                              <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                              <TestTube className="w-4 h-4" />
                            )}
                          </button>
                          
                          <button
                            onClick={() => handleClearKey(provider.id)}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>

                    {/* Test Result */}
                    {testResult && (
                      <div
                        className={`flex items-center gap-2 p-2 rounded-lg text-sm ${
                          testResult.success
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {testResult.success ? (
                          <Check className="w-4 h-4" />
                        ) : (
                          <AlertCircle className="w-4 h-4" />
                        )}
                        <span>{testResult.message}</span>
                      </div>
                    )}

                    {/* Status Indicator */}
                    {hasKey && !testResult && (
                      <div className="flex items-center gap-2 text-xs text-green-600">
                        <Check className="w-3 h-3" />
                        <span>API key configured</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Clear All Button */}
          <div className="pt-4 border-t">
            <button
              onClick={() => {
                if (window.confirm('Are you sure you want to clear all API keys?')) {
                  clearAllApiKeys();
                  setTempKeys({});
                }
              }}
              className="px-4 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg transition-colors flex items-center gap-2"
            >
              <Trash2 className="w-4 h-4" />
              Clear All API Keys
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApiSettingsModal;